import React, { useEffect, useLayoutEffect, useRef, useState, useMemo } from "react";
import styles from "./HelpSupport.module.css";
import {
  InputField,
  PhoneInput,
  SubmitButton,
  TextComponent,
} from "../../components";
import logo from "../../assets/images/logo.png";
import { Button, Drawer, List, Space } from "antd";
import { IoCloseCircleSharp, IoSend } from "react-icons/io5";
import { GrAttachment } from "react-icons/gr";
import userImage from "../../assets/images/staticImages/pf.png";
import useWindowDimensions from "../../hooks/useWindowDimensions";
import { BsChatLeftDotsFill } from "react-icons/bs";
import { useDispatch, useSelector } from "react-redux";
import { AuthMiddleware } from "../../Store/Middlewares/AuthMiddleware";
import { showAlert } from "../../Store/Actions/GeneralActions";
import { phoneFormat } from "../../utills";
import { ChatMiddleware } from "../../Store/Middlewares/ChatMiddleware";
import { pusher } from "../../utills/PusherHandler";
import { updateChats } from "../../Store/Actions/ChatAction";

const HelpSupport = () => {
  const [email, setEmail] = useState("");
  const [contactNo, setContactNo] = useState("");
  const [message, setMessage] = useState("");
  const [query, setQuery] = useState("");
  const [openDrawer, setOpenDrawer] = useState(false);
  const [disableMessage, setDisableMessage] = useState(false);

  const { width } = useWindowDimensions();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.AuthReducer.user);
  const CHATS = useSelector((state) => state.ChatReducer?.chats) || [];
  const CHATS_PAGINATION = useSelector((state) => state.ChatReducer.chatPagination);

  const channelName = useRef();
  const listRef = useRef(null); // scroll container
  const messagesEndRef = useRef(null); // bottom anchor

  useEffect(() => {
    dispatch(AuthMiddleware.getUserData());
  }, [dispatch]);

  useEffect(() => {
    if (user?.id) {
      getChatList();
    }
  }, [user?.id]);

  // Keep a stable, chronological array (oldest -> newest)
  const sortedChats = useMemo(() => {
    if (!CHATS) return [];
    // If you have created_at use that instead, e.g. new Date(a.created_at) - new Date(b.created_at)
    return [...CHATS].sort((a, b) => (a.id || 0) - (b.id || 0));
  }, [CHATS]);

  const scrollToBottom = (behavior = "smooth") => {
    const container = listRef.current;
    if (container) {
      // scroll container to its bottom
      try {
        container.scrollTo({ top: container.scrollHeight, behavior });
      } catch (e) {
        container.scrollTop = container.scrollHeight;
      }
    } else {
      messagesEndRef.current?.scrollIntoView({ behavior, block: "end" });
    }
  };

  // run after DOM updates when chats change
  useLayoutEffect(() => {
    // use 'auto' for initial mount to avoid animation jump
    scrollToBottom("auto");
  }, [sortedChats.length, openDrawer]);

  useEffect(() => {
    initiatePusher();

    return () => {
      if (channelName?.current) {
        try {
          channelName.current.unsubscribe();
        } catch (e) {
          // ignore
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, openDrawer]);

  const initiatePusher = () => {
    if (!user?.id || !pusher) return;
    // unsubscribe previous if exists
    try {
      if (channelName.current) {
        channelName.current.unsubscribe();
      }
    } catch (e) {}
    channelName.current = pusher.subscribe(user.id.toString());
    channelName.current.bind("App\\Events\\SendMessage", (data) => {
      if (user?.id == data?.message?.user_id) {
        dispatch(updateChats(data?.message));
        // no need for manual scroll here — sortedChats change will trigger useLayoutEffect
      }
    });
  };

  const getChatList = () => {
    dispatch(ChatMiddleware.getChatMessages({ url: undefined })).then(() => {
      // ensure scroll after list fetched
      setTimeout(() => scrollToBottom("auto"), 50);
    });
  };

  const onPressLoadMore = () => {
    if (CHATS_PAGINATION?.links?.next) {
      let chatdata = {
        next_page_url: CHATS_PAGINATION?.links?.next,
      };
      dispatch(ChatMiddleware.getChatMessages({ url: chatdata?.next_page_url }));
    }
  };

  const onSendMessage = () => {
    if (!message || disableMessage) return;
    const Data = {
      message: message,
      type: "text",
    };
    setDisableMessage(true);
    dispatch(ChatMiddleware.sendMessage(Data))
      .then((data) => {
        setMessage("");
        setDisableMessage(false);
        // small timeout to ensure redux updated, though useLayoutEffect on sortedChats should handle it
        setTimeout(() => scrollToBottom(), 50);
      })
      .catch((err) => {
        // reset input to empty string on error
        setMessage("");
        setDisableMessage(false);
      });
  };

  const Submit = () => {
    if (!query) {
      dispatch(
        showAlert({
          title: "Contact Us",
          message: "Please fill the message field",
          type: "Error",
          status: 400,
        })
      );
    } else {
      let userData = {
        message: query,
      };
      dispatch(AuthMiddleware.contactUs(userData))
        .then(() => {
          setQuery("");
        })
        .catch(() => {});
    }
  };

  const renderChats = (item, index) => {
    const key = item?.id ?? index;
    if (item?.sender === "admin") {
      return (
        <div className={styles.listItem1} key={key}>
          <img src={logo} alt="logo" />
          <div style={{ display: "flex", flexDirection: "column", width: "60%" }}>
            <TextComponent text={item?.message} className={styles.listMessage1} />
            <TextComponent text={item?.formatted_time} className={styles.timeadmin} />
          </div>
        </div>
      );
    }

    return (
      <div className={styles.listItem2} key={key}>
        <div style={{ display: "flex", flexDirection: "column", width: "60%" }}>
          <TextComponent text={item?.message} className={styles.listMessage2} />
          <TextComponent text={item?.formatted_time} className={styles.time} />
        </div>
        <img src={user?.profile_picture ? user.profile_picture : userImage} alt="user" />
      </div>
    );
  };

  const loadMore = CHATS_PAGINATION?.links?.next ? (
    <div
      style={{
        textAlign: "center",
        marginTop: 12,
        height: 32,
        lineHeight: "32px",
        display: "flex",
        justifyContent: "center",
      }}
    >
      <button onClick={onPressLoadMore} className={styles.loadMore_button}>
        Load More
      </button>
    </div>
  ) : null;

  return (
    <div className={styles.mainCont}>
      <div className={styles.contactUs}>
        <TextComponent
          text={"Contact us"}
          style={{
            fontFamily: "Urbanist-bold",
            fontSize: "var(--heading2-font-size)",
          }}
        />
        <TextComponent
          style={{
            fontFamily: "Urbanist-semibold",
            color: "var(--light-grey)",
            marginTop: "10px",
          }}
          text={"Send us your queries"}
        />
        <InputField
          label={"Email"}
          style={{ marginTop: "30px" }}
          placeholder={user?.email || "Enter your contact email"}
          value={email}
          disabled
          onChange={(e) => setEmail(e.target.value)}
        />
        <PhoneInput
          label={"Contact number"}
          placeholder={phoneFormat(user?.contact_no) || "Enter your number"}
          value={contactNo}
          disabled
          onChange={setContactNo}
        />
        <InputField
          label={"Your Message"}
          placeholder={"Write your message"}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          textArea
          rows={6}
        />
        <SubmitButton title={"Send"} style={{ marginTop: "10px" }} onClick={Submit} />
      </div>

      <div className={styles.supportDiv}>
        <TextComponent
          text={"Phone Support"}
          style={{
            fontFamily: "Urbanist-bold",
            fontSize: "var(--heading2-font-size)",
          }}
        />
        <TextComponent
          style={{
            marginTop: "10px",
            fontFamily: "Urbanist-bold",
            fontSize: "var(--dashboard_tabs-font-size)",
          }}
          text={`${"(636) 296-7838"} | ${"(978) 444-4055"}`}
        />

        {!openDrawer ? (
          <>
            {width > 500 ? (
              <div className={styles.liveChatDiv}>
                <div className={styles.liveChat_upper}>
                  <img src={logo} alt="logo" />
                  <TextComponent style={{ fontFamily: "Urbanist-bold" }} text={"Phoenix Support"} />
                </div>
                <SubmitButton
                  onClick={() => setOpenDrawer(true)}
                  title={"Start live chat"}
                  btnClass={styles.liveChatBtn}
                  secondaryBtn
                />
              </div>
            ) : (
              <BsChatLeftDotsFill className={styles.chatIcon} onClick={() => setOpenDrawer(true)} />
            )}
          </>
        ) : (
          <div className={styles.chatDiv}>
            <div className={styles.chat_header}>
              <div className={styles.chat_headerLeft}>
                <img src={logo} alt="logo" />
                <TextComponent text={"Phoenix Support"} style={{ fontFamily: "Urbanist-bold" }} />
                <TextComponent text={"● Live chat"} className={styles.chat_liveChat} />
              </div>
              <div className={styles.chat_headerRight} onClick={() => setOpenDrawer(false)}>
                <IoCloseCircleSharp size={25} color={"var(--light-grey)"} />
              </div>
            </div>

            {/* Scrollable list container (give this a fixed height + overflow-y in CSS) */}
            <div ref={listRef} className={styles.content}>
              <List dataSource={sortedChats} renderItem={renderChats} header={loadMore} />
              {/* single bottom anchor */}
              <div ref={messagesEndRef} />
            </div>

            <div className={styles.typeBox}>
              {/* <div className={styles.typeBox_left}>
                <GrAttachment />
              </div> */}
              <div className={styles.typeBox_middle}>
                <input
                  disabled={disableMessage}
                  placeholder="type..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyDown={(event) => {
                    // send on Enter (without shift)
                    if (event.key === "Enter" && !event.shiftKey) {
                      event.preventDefault();
                      onSendMessage();
                    }
                  }}
                />
              </div>
              <div className={styles.typeBox_right}>
                <IoSend size={18} color={"var(--secondary-color)"} onClick={onSendMessage} />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HelpSupport;